<script lang="ts" setup>
import type { AddressSuggestionType, LocationItemType } from '~/api/common';
import type { UserAddressType } from '~/api/user';
import UserAPI, {
  createAddressBodySchema,
  houseAddressSchema,
} from '~/api/user';
import type { ResponseError } from '~/api/utils/types';

const TEST_ID = 'profile-address-form';

const props = defineProps<{
  address?: UserAddressType;
}>();
const emit = defineEmits<{
  (event: 'completed', value: { addressId?: number }): void;
}>();

const { t } = useI18n();
const modalStore = useModalStore();
const userStore = useUserStore();

const location = ref<LocationItemType>();
const cityQuery = ref('');
const addressQuery = ref('');
const street = ref('');
const house = ref('');
const flat = ref('');
const zip = ref('');
const intercom = ref('');
const floor = ref('');
const entrance = ref('');
const comment = ref('');

const isLoading = ref(false);

const body = computed(() => ({
  locationCode: location.value?.code || '',
  street: street.value,
  house: house.value,
  flat: flat.value,
  zip: zip.value,
  intercom: intercom.value,
  floor: floor.value,
  entrance: entrance.value,
  comment: comment.value,
}));
const isValid = computed(() => createAddressBodySchema.isValidSync(body.value));

const preventSearchAddressClosing = (value: AddressSuggestionType) =>
  !houseAddressSchema.isValidSync(value.data);

const onSelectLocation = (value: LocationItemType) => {
  location.value = value;
};

const onSelectAddressSuggestion = (value: AddressSuggestionType) => {
  street.value = value.data.streetWithType || street.value;
  house.value =
    [
      value.data.house,
      value.data.block && `${value.data.blockType} ${value.data.block}`,
    ]
      .filter(Boolean)
      .join(' ') || house.value;
  flat.value = value.data.flat || flat.value;
  zip.value = value.data.postalCode || zip.value;
};

const clear = () => {
  location.value = undefined;
  cityQuery.value = '';
  addressQuery.value = '';
  street.value = '';
  house.value = '';
  flat.value = '';
  zip.value = '';
  intercom.value = '';
  floor.value = '';
  entrance.value = '';
  comment.value = '';
};

const onSubmit = async () => {
  if (!isValid.value || isLoading.value) return;
  isLoading.value = true;

  try {
    let addressId: number | undefined;

    if (props.address) {
      addressId = props.address.id;
      userStore.profile.addresses = await UserAPI.updateAddress({
        params: { id: props.address.id },
        body: body.value,
      });

      modalStore.showSuccess({
        title: t('message.update_success'),
        action: t('message.return'),
      });
    } else {
      const addresses = await UserAPI.createAddress({
        body: body.value,
      });
      addressId = addresses.find(
        (el) => !userStore.profile.addresses.some((f) => f.id === el.id),
      )?.id;
      userStore.profile.addresses = addresses;

      modalStore.showSuccess({
        title: t('message.create_success'),
        action: t('message.return'),
      });
    }

    clear();
    emit('completed', { addressId });
  } catch (e) {
    modalStore.showError({
      title: getFetchErrorMessage(e as ResponseError, t('message.error')),
      action: t('message.return'),
    });
  }

  isLoading.value = false;
};

watch(
  () => props.address,
  (v) => {
    if (v) {
      cityQuery.value = v.city;
      addressQuery.value = userAddressToString(v, { city: false, flat: false });
      street.value = v.street;
      house.value = v.house;
      flat.value = v.flat || '';
      zip.value = v.zip || '';
      intercom.value = v.intercom || '';
      floor.value = v.floor || '';
      entrance.value = v.entrance || '';
      comment.value = v.comment || '';
    } else clear();
  },
  { immediate: true },
);
</script>

<template>
  <form
    @submit.prevent="onSubmit"
    class="profileAddressForm"
    :data-test-id="TEST_ID"
  >
    <div class="profileAddressForm__fields">
      <BaseSearchCity
        v-model:query="cityQuery"
        @select="onSelectLocation"
        :data-test-id="`${TEST_ID}-city`"
      />
      <BaseSearchAddress
        v-model:query="addressQuery"
        @select="onSelectAddressSuggestion"
        :search-prefix="location?.name"
        :disabled="!location"
        :prevent-closing="preventSearchAddressClosing"
        :data-test-id="`${TEST_ID}-address`"
      />
      <div class="profileAddressForm__row">
        <BaseTextField
          v-model:value="flat"
          :placeholder="t('form.flat_placeholder')"
          bordered
          :data-test-id="`${TEST_ID}-flat`"
        />
        <BaseTextField
          v-model:value="intercom"
          :placeholder="t('form.intercom_placeholder')"
          bordered
          :data-test-id="`${TEST_ID}-intercom`"
        />
      </div>
      <div class="profileAddressForm__row">
        <BaseTextField
          v-model:value="entrance"
          :placeholder="t('form.entrance_placeholder')"
          bordered
          :data-test-id="`${TEST_ID}-entrance`"
        />
        <BaseTextField
          v-model:value="floor"
          :placeholder="t('form.floor_placeholder')"
          bordered
          :data-test-id="`${TEST_ID}-floor`"
        />
      </div>
      <BaseTextField
        v-model:value="comment"
        rows="1"
        multiline
        :placeholder="t('form.comment_placeholder')"
        bordered
        :data-test-id="`${TEST_ID}-comment`"
      />
    </div>

    <BaseButton
      dark
      block
      :disabled="!isValid"
      :loading="isLoading"
      type="submit"
      :data-test-id="`${TEST_ID}-submit`"
    >
      {{ t('form.submit') }}
    </BaseButton>
  </form>
</template>

<i18n>
ru:
  form:
    flat_placeholder: Квартира
    intercom_placeholder: Домофон
    entrance_placeholder: Подъезд
    floor_placeholder: Этаж
    comment_placeholder: Комментарий для курьера
    submit: Сохранить
  message:
    update_success: Адрес успешно обновлен
    create_success: Новый адрес успешно добавлен
    error: Произошла ошибка, повторите попытку позже
    return: Вернуться

en:
  form:
    flat_placeholder: Flat
    intercom_placeholder: Intercom
    entrance_placeholder: Entrance
    floor_placeholder: Floor
    comment_placeholder: Comment for courier
    submit: Save
  message:
    update_success: Address successfully updated
    create_success: New address successfully added
    error: An error occurred, please try again later
    return: Return
</i18n>

<style lang="scss">
.profileAddressForm {
  &__fields {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 20px;
  }

  &__row {
    display: flex;
    gap: 16px;

    & > * {
      flex: 1;
      width: 50%;
    }
  }
}
</style>
